/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Flip Transform Node
   Flips image horizontally and/or vertically on GPU.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const FLIP_FRAGMENT_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform bool u_flipX;
uniform bool u_flipY;

void main() {
  vec2 uv = v_uv;
  if (u_flipX) uv.x = 1.0 - uv.x;
  if (u_flipY) uv.y = 1.0 - uv.y;
  fragColor = texture(u_image, uv);
}
`;

export const flipNode: NodeDefinition = {
  type: 'transform.flip',
  category: 'Transform',
  label: 'Flip',
  description: 'Mirrors the image horizontally or vertically',
  keywords: ['flip', 'mirror', 'invert', 'horizontal', 'vertical'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Flipped', type: 'image' },
  ],
  params: [
    {
      id: 'flipX',
      name: 'Horizontal Mirror',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
    {
      id: 'flipY',
      name: 'Vertical Mirror',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: FLIP_FRAGMENT_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_flipX: Boolean(params.flipX),
      u_flipY: Boolean(params.flipY),
    }),
  }),
};
